import { logger } from './logger'

export interface ApiError {
    message: string
    status?: number
    code?: string
    details?: any
    isRetryable?: boolean
    userMessage?: string
}

export interface RetryConfig {
    maxRetries: number
    baseDelay: number
    maxDelay: number
    backoffMultiplier: number
}

export class ApiErrorHandler {
    private static defaultRetryConfig: RetryConfig = {
        maxRetries: 3,
        baseDelay: 1000, // 1 second
        maxDelay: 10000, // 10 seconds
        backoffMultiplier: 2
    }

    /**
     * Standardizes API errors into a consistent format
     */
    static createApiError(error: any): ApiError {
        // Handle different error types
        if (error instanceof Error) {
            return {
                message: error.message,
                status: (error as any).status,
                code: (error as any).code,
                details: error,
                isRetryable: this.isRetryableError(error),
                userMessage: this.getUserFriendlyMessage(error)
            }
        }

        // Handle HTTP Response errors
        if (error && typeof error === 'object' && error.status) {
            return {
                message: error.message || error.statusText || 'Request failed',
                status: error.status,
                code: error.code,
                details: error,
                isRetryable: this.isRetryableError(error),
                userMessage: this.getUserFriendlyMessage(error)
            }
        }

        // Handle string errors
        if (typeof error === 'string') {
            return {
                message: error,
                isRetryable: false,
                userMessage: 'Something went wrong. Please try again.'
            }
        }

        // Fallback for unknown error types
        return {
            message: 'An unknown error occurred',
            details: error,
            isRetryable: false,
            userMessage: 'Something unexpected happened. Please try again.'
        }
    }

    /**
     * Determines if an error is retryable based on status code and error type
     */
    static isRetryableError(error: any): boolean {
        const status = error?.status || error?.response?.status

        if (!status) return false

        // Network errors are often retryable
        if (status === 0 || !navigator.onLine) return true

        // Server errors (5xx) are retryable
        if (status >= 500) return true

        // Rate limiting (429) is retryable
        if (status === 429) return true

        // Timeout errors are retryable
        if (error.code === 'TIMEOUT' || error.name === 'TimeoutError') return true

        // Client errors (4xx) are generally not retryable
        return false
    }

    /**
     * Provides user-friendly error messages based on status codes
     */
    static getUserFriendlyMessage(error: any): string {
        const status = error?.status || error?.response?.status

        switch (status) {
            case 400:
                return 'Invalid request. Please check your input and try again.'
            case 401:
                return 'You need to be logged in to perform this action.'
            case 403:
                return 'You don\'t have permission to perform this action.'
            case 404:
                return 'The requested item could not be found.'
            case 408:
                return 'Request timed out. Please try again.'
            case 429:
                return 'Too many requests. Please wait a moment and try again.'
            case 500:
            case 502:
            case 503:
            case 504:
                return 'Server error. Please try again in a few moments.'
            default:
                if (!navigator.onLine) {
                    return 'No internet connection. Please check your connection and try again.'
                }
                return 'Something went wrong. Please try again.'
        }
    }

    /**
     * Calculates retry delay with exponential backoff
     */
    static calculateRetryDelay(attempt: number, config: RetryConfig = this.defaultRetryConfig): number {
        const delay = Math.min(
            config.baseDelay * Math.pow(config.backoffMultiplier, attempt),
            config.maxDelay
        )
        
        // Add jitter to prevent thundering herd
        return delay + Math.random() * 1000
    }

    /**
     * Wraps an API call with retry logic
     */
    static async withRetry<T>(
        apiCall: () => Promise<T>,
        config: Partial<RetryConfig> = {},
        context: string = 'api_call'
    ): Promise<T> {
        const retryConfig = { ...this.defaultRetryConfig, ...config }
        let lastError: ApiError | null = null

        for (let attempt = 0; attempt <= retryConfig.maxRetries; attempt++) {
            try {
                logger.api.request(context, attempt === 0 ? 'GET' : 'RETRY')
                const result = await apiCall()
                
                if (attempt > 0) {
                    logger.info('api', `${context} succeeded after ${attempt} retries`)
                }
                
                return result
            } catch (error) {
                lastError = this.createApiError(error)
                
                logger.api.error(context, lastError)

                // Don't retry if error is not retryable or we've exhausted retries
                if (!lastError.isRetryable || attempt >= retryConfig.maxRetries) {
                    break
                }

                const delay = this.calculateRetryDelay(attempt, retryConfig)
                logger.info('api', `${context} retrying in ${delay}ms (attempt ${attempt + 1}/${retryConfig.maxRetries})`)
                
                await new Promise(resolve => setTimeout(resolve, delay))
            }
        }

        // All retries exhausted
        throw lastError || this.createApiError('Unknown error occurred')
    }

    /**
     * Enhanced fetch wrapper with error handling and retry logic
     */
    static async enhancedFetch(
        url: string,
        options: RequestInit = {},
        retryConfig?: Partial<RetryConfig>
    ): Promise<Response> {
        const context = `${options.method || 'GET'} ${url}`

        return this.withRetry(async () => {
            const controller = new AbortController()
            const timeoutId = setTimeout(() => controller.abort(), 30000) // 30 second timeout

            try {
                const response = await fetch(url, {
                    ...options,
                    signal: controller.signal
                })

                clearTimeout(timeoutId)

                if (!response.ok) {
                    throw {
                        message: `HTTP ${response.status}: ${response.statusText}`,
                        status: response.status,
                        statusText: response.statusText,
                        response
                    }
                }

                logger.api.response(context, response.status)
                return response
            } catch (error) {
                clearTimeout(timeoutId)
                
                if ((error as any).name === 'AbortError') {
                    throw {
                        message: 'Request timed out',
                        code: 'TIMEOUT',
                        status: 408
                    }
                }
                
                throw error
            }
        }, retryConfig, context)
    }
}

export default ApiErrorHandler